<template>
    <div data-cy="metadata-value-select--dropdown">
        <pendo-multiselect
            :allow-empty="false"
            :append-to-body="false"
            :loading="isLoading"
            :disable-while-loading="false"
            :disabled="readOnly"
            :extra-label-width="60"
            :internal-search="!limitExceeded"
            :max-menu-height="maxHeightWithSidePanel"
            min-trigger-width="80px"
            :min-menu-width="menuWidth"
            :max-menu-width="menuWidth"
            :options="options"
            placeholder="Type value here"
            searchable
            taggable
            tag-placeholder="(new)"
            :value="value"
            @input-value-change="onInputValueChanged"
            @select="onOptionSelected"
            @tag="onOptionSelected">
            <template #header>
                <div
                    v-if="showSearchHeader"
                    class="metadata-value-select--search-notice"
                    data-cy="metadata-value-select--search-notice">
                    <pendo-alert :show-icon="false">
                        Begin your search with the <strong>first case-sensitive letters</strong> of a value (e.g. “Pen”
                        for “Pendo”)
                    </pendo-alert>
                </div>
                <p
                    v-if="limitExceeded"
                    class="metadata-value-select--limit-notice"
                    data-cy="metadata-value-select--limit-notice">
                    Showing first 400 results only
                </p>
            </template>
            <template #noData>
                <p class="metadata-value-select--empty-state">
                    No options available
                </p>
            </template>
            <template
                v-if="showSidePanel"
                #aside="{ highlightedOption }">
                <account-visitor-side-panel
                    :id="optionId(highlightedOption)"
                    :kind="dropdownKind" />
            </template>
        </pendo-multiselect>
    </div>
</template>

<script>
import { PendoAlert, PendoMultiselect, PendoNotification } from '@pendo/components';
import { MOBILE_QUERY_FIELDS, queryMetadataValues } from '@/vuex/modules/metadata/metadata.utils';
import { MOBILE_VERSION_RULES_LIST } from '@/utils/segments.js';
import clone from 'lodash/clone';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import AccountVisitorSidePanel from '@/common/account-visitor-side-panel/account-visitor-side-panel.vue';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { useWindowSize } from '@vueuse/core';
import { shouldShowAsidePanel as shouldShowAsidePanelUtil } from '@/utils/responsive-multiselect.utils';

export default {
    name: 'MetadataValueSelect',
    components: {
        PendoAlert,
        PendoMultiselect,
        AccountVisitorSidePanel,
    },
    props: {
        field: {
            type: String,
            default: 'value',
        },
        rule: {
            type: Object,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        value: {
            type: String,
            default: '',
        },
    },
    emits: ['input'],
    setup () {
        const { width: windowWidth } = useWindowSize();

        return {
            windowWidth,
        };
    },
    data () {
        return {
            isLoading: true,
            initialLimitExceeded: false,
            initialOptions: [],
            limitExceeded: false,
            options: [],
            searchValue: '',
        };
    },
    computed: {
        queryProps () {
            const { id, kind, group, field } = this.rule;

            if (MOBILE_VERSION_RULES_LIST.includes(id)) {
                return {
                    kind,
                    group: 'auto',
                    field: MOBILE_QUERY_FIELDS[field],
                };
            }

            return {
                kind,
                group,
                field: field.split('.').pop(),
            };
        },
        showSearchHeader () {
            return this.searchValue === '' && this.initialLimitExceeded;
        },
        maxMenuHeight () {
            const defaultHeight = 302;
            const searchNoticeHeight = 100;
            const limitNoticeHeight = 30;

            if (this.showSearchHeader) {
                return defaultHeight + searchNoticeHeight + limitNoticeHeight;
            }

            return this.limitExceeded ? defaultHeight + limitNoticeHeight : defaultHeight;
        },
        maxHeightWithSidePanel () {
            return this.showSidePanel ? 400 : this.maxMenuHeight;
        },
        isVisitorIdDropdown () {
            return this.rule.id === 'visitor_auto_id';
        },
        isAccountIdDropdown () {
            return this.rule.id === 'account_auto_id';
        },
        showSidePanel () {
            const isAEU = this.$productLine(PRODUCT_LINES.ADOPT);

            return !isAEU && shouldShowAsidePanelUtil(this.windowWidth) && (this.isVisitorIdDropdown || this.isAccountIdDropdown);
        },
        dropdownKind () {
            return this.isVisitorIdDropdown ? 'visitor' : 'account';
        },
        menuWidth () {
            return this.showSidePanel ? 720 : undefined;
        },
    },
    watch: {
        'rule.appId': function () {
            this.getOptions(this.searchValue);
        },
    },
    async created () {
        this.getOptionsDebounce = debounce(this.getOptions, 500);
        await this.getOptions('');
        this.initialLimitExceeded = this.limitExceeded;
        if (this.initialLimitExceeded) {
            this.initialOptions = clone(this.options);
        }

        const value = isString(this.value) ? this.value : '';
        this.onOptionSelected(value);
    },
    methods: {
        async getOptions (query) {
            if (query === '' && this.initialOptions.length !== 0) {
                this.limitExceeded = this.initialLimitExceeded;
                this.options = clone(this.initialOptions);
                this.isLoading = false;

                return;
            }

            this.isLoading = true;

            try {
                const { appId } = this.rule;
                const { kind, group, field } = this.queryProps;
                const { data } = await queryMetadataValues(kind, group, field, {
                    filter: query,
                    app: appId,
                });

                this.limitExceeded = data.limitExceeded;
                this.options = data.values;
            } catch {
                PendoNotification({
                    type: 'error',
                    message: 'Unable to fetch metadata values',
                    duration: 5000,
                });
            }

            this.isLoading = false;
        },
        onInputValueChanged (inputValue) {
            if (this.initialLimitExceeded) {
                this.searchValue = inputValue;
                this.isLoading = true;
                this.getOptionsDebounce(inputValue);
            }
        },
        onOptionSelected (value) {
            this.$emit('input', { field: this.field, value });
        },
        optionId (option) {
            return typeof option === 'string' ? option : undefined;
        },
    },
};
</script>

<style lang="less" scoped>
.metadata-value-select {
    &--empty-state {
        color: var(--palette-gray-70);
        font-size: 14px;
        line-height: 21px;
        margin: 0;
        text-align: center;
    }

    &--limit-notice {
        font-weight: bold;
        margin: 0;
        padding: 8px 16px;
        text-align: center;
    }

    &--search-notice {
        padding: 16px 16px 8px;
    }
}
</style>
